/**
 * ArchivedAdminsPage — list archived admin users with restore + hard-delete.
 * Hard-delete requires typing 'DELETE' in confirmation.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, RotateCcw, Trash2, Search, Users, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ArchivedAdminsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/archive/admins`, { headers: getAuthHeaders() });
      setItems(res.data?.data || []);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل تحميل الأرشيف' : 'Failed to load archive');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const restore = async (u) => {
    setProcessingId(u.id);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/archive/admins/${u.id}/restore`, {}, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم استعادة الحساب' : 'Restored');
      fetchItems();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || (language === 'ar' ? 'فشل الاستعادة' : 'Restore failed'));
      toast.error(msg, { duration: 6000 });
    } finally {
      setProcessingId(null);
    }
  };

  const hardDelete = async () => {
    if (!confirmDelete || confirmInput.trim().toUpperCase() !== 'DELETE') return;
    setProcessingId(confirmDelete.id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/archive/admins/${confirmDelete.id}`, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم الحذف النهائي' : 'Permanently deleted');
      setConfirmDelete(null);
      setConfirmInput('');
      fetchItems();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || (language === 'ar' ? 'فشل الحذف' : 'Delete failed'));
      toast.error(msg, { duration: 6000 });
    } finally {
      setProcessingId(null);
    }
  };

  const visible = items.filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [u.name, u.email, u.phone, u.platform].some((v) => v && String(v).toLowerCase().includes(q));
  });

  return (
    <DashboardLayout
      title="أرشيف حسابات الموظفين والمشرفين"
      titleEn="Archived Admin Accounts"
      subtitle={language === 'ar' ? `${items.length} حساب مؤرشف` : `${items.length} archived accounts`}
      subtitleEn={`${items.length} archived accounts`}
      screenCode="ARC-004"
      backPath="/control/archive/asb606-admin"
      onBack={() => navigate('/control/archive/asb606-admin')}
      breadcrumb={[
        { labelAr: 'الأرشيف', labelEn: 'Archive', path: '/control/archive' },
        { labelAr: 'ASB606 ADMIN', labelEn: 'ASB606 ADMIN', path: '/control/archive/asb606-admin' },
        { labelAr: 'حسابات الإدارة', labelEn: 'Admin Accounts' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="archived-admins-page">
        <div className="relative max-w-md mb-4">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={language === 'ar' ? 'بحث بالاسم/البريد/الهاتف...' : 'Search by name/email/phone...'}
            className="w-full ps-3 pe-9 py-2.5 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm focus:border-blue-500/50 focus:outline-none"
            data-testid="archived-admin-search"
          />
        </div>

        <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-400 mx-auto" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16 text-[#808080]">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{language === 'ar' ? 'لا توجد حسابات مؤرشفة' : 'No archived accounts'}</p>
            </div>
          ) : (
            <div className="divide-y divide-[#2a2a4e]/50">
              {visible.map((u) => (
                <div
                  key={u.id}
                  className="p-4 flex items-center justify-between gap-3 hover:bg-[#0a0a0a]/40"
                  data-testid={`archived-admin-row-${u.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium">{u.name || u.email || '—'}</span>
                      {u.platform && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#d4af37]/10 text-[#d4af37]">
                          {u.platform}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#808080] mt-1 flex items-center gap-2 flex-wrap">
                      {u.email && <span>{u.email}</span>}
                      {u.phone && <span dir="ltr">{u.phone}</span>}
                      {u.archived_at && (
                        <span className="text-blue-400">
                          {language === 'ar' ? 'أُرشف:' : 'archived:'} {new Date(u.archived_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {u.archived_reason && (
                      <p className="text-xs text-[#606060] mt-1">
                        {language === 'ar' ? 'السبب: ' : 'Reason: '}{u.archived_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => restore(u)}
                      disabled={processingId === u.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                      data-testid={`restore-admin-${u.id}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5 me-1" />
                      {language === 'ar' ? 'استعادة' : 'Restore'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setConfirmDelete(u); setConfirmInput(''); }}
                      disabled={processingId === u.id}
                      variant="outline"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8"
                      data-testid={`harddel-admin-${u.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 me-1" />
                      {language === 'ar' ? 'حذف نهائي' : 'Delete'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Hard-delete confirm dialog */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setConfirmInput(''); } }}
      >
        <DialogContent className="bg-[#1a1a2e] border-red-500/30 text-white" data-testid="harddel-admin-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {language === 'ar' ? 'تأكيد الحذف النهائي' : 'Confirm permanent delete'}
            </DialogTitle>
            <DialogDescription className="text-[#a0a0a0]">
              {language === 'ar'
                ? <>سيتم حذف <span className="text-white">{confirmDelete?.name || confirmDelete?.email}</span> نهائياً ولا يمكن استعادته. اكتب <code className="text-red-400 font-mono mx-1">DELETE</code> للتأكيد.</>
                : <>This will permanently delete <span className="text-white">{confirmDelete?.name || confirmDelete?.email}</span>. Type <code className="text-red-400 font-mono mx-1">DELETE</code> to confirm.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder="DELETE"
              className="bg-[#0a0a0a] border-red-500/30 text-white font-mono"
              data-testid="harddel-admin-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setConfirmDelete(null); setConfirmInput(''); }}
              className="border-[#2a2a4e] text-white"
              data-testid="harddel-admin-cancel"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={hardDelete}
              disabled={confirmInput.trim().toUpperCase() !== 'DELETE' || processingId === confirmDelete?.id}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="harddel-admin-confirm-btn"
            >
              {processingId === confirmDelete?.id
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Trash2 className="h-4 w-4 me-2" />{language === 'ar' ? 'حذف نهائي' : 'Delete forever'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
